/**
 * Write/delete churn measurement — the one place both execution paths (the local
 * agent loop and the gateway client) size a file change for the verification-need
 * gate. Kept apart from the pure change-significance policy because it touches the
 * filesystem: it reads the file's CURRENT contents (the baseline, before the tool
 * replaces it) and diffs them against the new contents.
 */
import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';
import { lineChurn, type ChangedFile } from './change-significance.js';

/**
 * Measure a write_file / delete_file as a {@link ChangedFile} record. MUST be
 * called BEFORE the tool runs, so the baseline is the pre-change file. Returns
 * `{}` for tools that don't change a single known path (e.g. terminal_exec) —
 * the caller treats those as opaque mutations. Best-effort: an unreadable file
 * leaves churn undefined (the gate then treats the change as non-trivial).
 */
export function measureFileChange(
  repoRoot: string,
  toolName: string,
  args: { path?: unknown; content?: unknown },
): ChangedFile | null {
  if (toolName !== 'write_file' && toolName !== 'delete_file') return null;
  const path = typeof args.path === 'string' ? args.path : undefined;
  if (!path) return null;

  const deleted = toolName === 'delete_file';
  let before: string | null = '';
  let existed = false;
  try {
    const abs = join(repoRoot, path);
    existed = existsSync(abs);
    before = existed ? readFileSync(abs, 'utf8') : '';
  } catch {
    before = null; // unreadable → size unknown
  }

  let churn: number | undefined;
  if (before !== null) {
    churn = deleted
      ? before
        ? before.split('\n').length
        : 0
      : lineChurn(before, typeof args.content === 'string' ? args.content : '');
  }

  return {
    path,
    ...(churn !== undefined ? { churn } : {}),
    ...(deleted ? { deleted: true } : {}),
    // A write to a path that did not exist is a NEW file (delete is never "created").
    ...(!deleted && !existed ? { created: true } : {}),
  };
}
